import React from "react";
import { useFormikContext } from "formik";
import Select from "react-select";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "../forms.module.css";
import { TCompanyLabeledOption } from "../../../services/types/users.types";
import { TContactPerson } from "../../../services/types/user.types";
import { DeviceCategory, DeviceConfig } from "../../../services/types/device.types";
import {
  DEVICE_CATEGORIES,
  getDefaultRegistrationConfig,
  getDeviceCapabilities,
  normalizeRegistrationConfig,
} from "../../../utils/deviceConfiguration";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../services/selectors/utils.selectors";
import { useGetAllMetaTablesQuery } from "../../../services/api/utils.api";

interface DeviceContactSectionProps {
  index: number;
  companiesOptions: TCompanyLabeledOption[];
  handleChange: (index: number, field: string, value: unknown) => void;
}

function DeviceContactSection({
  index,
  companiesOptions,
  handleChange,
}: DeviceContactSectionProps) {
  const { values } = useFormikContext<{ contacts: TContactPerson[] }>();
  const contact = values.contacts?.[index];
  const capabilities = getDeviceCapabilities(contact?.deviceConfig);
  const treatmentRoomId = contact?.deviceConfig?.treatmentRoomId ?? null;

  const admin = useAppSelector(adminSelector);
  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });
  const meta = useAppSelector(appointmentsProgramsDataSelector);

  // Both fields live in the same config object, so each write has to carry the other.
  const patchDeviceConfig = (patch: Partial<DeviceConfig>) => {
    handleChange(index, "deviceConfig", { capabilities, treatmentRoomId, ...patch });
  };

  const toggleCapability = (cap: DeviceCategory) => {
    patchDeviceConfig({
      capabilities: capabilities.includes(cap)
        ? capabilities.filter((c) => c !== cap)
        : [...capabilities, cap],
    });
  };

  return (
    <div className={styles.form__section}>
      <ul className={`${styles.form__list} ${styles.form__list_type_fourColumns}`}>
        <li>
          <label className={styles.form__label}>{t("DEVICE_NAME")}</label>
          <input
            type="text"
            className={styles.form__input}
            value={contact?.firstName ?? ""}
            onChange={(e) => handleChange(index, "firstName", e.target.value)}
            placeholder={t("DEVICE_NAME_PLACEHOLDER")}
          />
        </li>
        <li>
          <label className={styles.form__label}>{t("SUBSIDIARY")}</label>
          <Select
            name={`contacts.${index}.fakeCompanyId`}
            inputId={`contacts.${index}.fakeCompanyId`}
            options={companiesOptions}
            value={
              companiesOptions.find((c) => c.value === contact?.fakeCompanyId) || undefined
            }
            placeholder={t("PLEASE_SELECT_SUBSIDIARY")}
            className={styles.select}
            onChange={(value) => {
              if (value) {
                handleChange(
                  index,
                  "fakeCompanyId",
                  value.Id === -2 ? null : value.Id,
                );
              }
            }}
          />
        </li>
        <li>
          <label className={styles.form__label}>{t("TREATMENT_ROOM")}</label>
          <Select
            name={`contacts.${index}.treatmentRoomId`}
            inputId={`contacts.${index}.treatmentRoomId`}
            options={meta.TreatmentRoomsList.map((room) => ({
              value: room.Id,
              label: room.Name,
            }))}
            value={
              meta.TreatmentRoomsList.filter((room) => room.Id === treatmentRoomId).map((room) => ({
                value: room.Id,
                label: room.Name,
              }))[0] ?? null
            }
            placeholder={t("SELECT_TREATMENT_ROOM")}
            className={styles.select}
            isClearable
            onChange={(value) =>
              patchDeviceConfig({
                treatmentRoomId: (value as { value: number } | null)?.value ?? null,
              })
            }
          />
        </li>
      </ul>
      <div style={{ marginTop: 16 }}>
        <label className={styles.form__label}>{t("DEVICE_CAPABILITIES")}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
          {DEVICE_CATEGORIES.map((c) => (
            <label
              key={c.value}
              style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={capabilities.includes(c.value)}
                onChange={() => toggleCapability(c.value)}
              />
              {t(c.labelKey)}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DeviceContactSection;

export function createDefaultDeviceContact(): TContactPerson {
  return {
    firstName: "",
    fakeCompanyId: null,
    relation: "device",
    statusId: 1,
    roleId: 7,
    deviceConfig: getDefaultRegistrationConfig(),
  };
}

export { normalizeRegistrationConfig };
